import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import Icon from '../../../components/AppIcon';

const AnalyticsChart = () => {
  const screeningData = [
    { month: 'Jan', applications: 180, screened: 142, hired: 8 },
    { month: 'Feb', applications: 220, screened: 189, hired: 12 },
    { month: 'Mar', applications: 195, screened: 156, hired: 9 },
    { month: 'Apr', applications: 267, screened: 234, hired: 15 },
    { month: 'May', applications: 289, screened: 251, hired: 18 },
    { month: 'Jun', applications: 247, screened: 189, hired: 12 }
  ];

  const accuracyData = [
    { week: 'Week 1', accuracy: 78 },
    { week: 'Week 2', accuracy: 82 },
    { week: 'Week 3', accuracy: 85 },
    { week: 'Week 4', accuracy: 89 },
    { week: 'Week 5', accuracy: 92 },
    { week: 'Week 6', accuracy: 94 }
  ];

  const teamPerformance = [
    { name: 'Sarah Wilson', processed: 89, accuracy: 94, interviews: 23 },
    { name: 'Michael Brown', processed: 76, accuracy: 91, interviews: 19 },
    { name: 'Emma Johnson', processed: 82, accuracy: 88, interviews: 21 },
    { name: 'David Chen', processed: 67, accuracy: 92, interviews: 16 }
  ];

  return (
    <div className="space-y-6">
      {/* Screening Trends */}
      <div className="bg-card border border-border rounded-lg p-6 card-elevation-base">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Screening Trends</h3>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-primary rounded-full"></div>
              <span className="text-xs text-muted-foreground">Applications</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-secondary rounded-full"></div>
              <span className="text-xs text-muted-foreground">Screened</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-success rounded-full"></div>
              <span className="text-xs text-muted-foreground">Hired</span>
            </div>
          </div>
        </div>
        
        <div className="w-full h-64" aria-label="Monthly Screening Trends Bar Chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={screeningData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="month" 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--color-popover)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  color: 'var(--color-popover-foreground)'
                }}
              />
              <Bar dataKey="applications" fill="var(--color-primary)" radius={[2, 2, 0, 0]} />
              <Bar dataKey="screened" fill="var(--color-secondary)" radius={[2, 2, 0, 0]} />
              <Bar dataKey="hired" fill="var(--color-success)" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* AI Accuracy Trends */}
      <div className="bg-card border border-border rounded-lg p-6 card-elevation-base">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="Brain" size={16} color="var(--color-accent)" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">AI Screening Accuracy</h3>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-success">94%</p>
            <p className="text-xs text-muted-foreground">Current Week</p>
          </div>
        </div>
        
        <div className="w-full h-48" aria-label="AI Screening Accuracy Line Chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={accuracyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="week" 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <YAxis 
                domain={[70, 100]}
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--color-popover)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  color: 'var(--color-popover-foreground)'
                }}
                formatter={(value) => [`${value}%`, 'Accuracy']}
              />
              <Line 
                type="monotone" 
                dataKey="accuracy" 
                stroke="var(--color-accent)" 
                strokeWidth={3}
                dot={{ fill: 'var(--color-accent)', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: 'var(--color-accent)', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Team Performance */}
      <div className="bg-card border border-border rounded-lg p-6 card-elevation-base">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Team Performance</h3>
          <button className="text-sm text-primary hover:text-primary/80 font-medium transition-colors">
            View Full Report
          </button>
        </div>
        
        <div className="space-y-4">
          {teamPerformance?.map((member, index) => (
            <div key={index} className="flex items-center justify-between p-4 rounded-lg hover:bg-muted/50 transition-colors">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} color="var(--color-primary)" />
                </div>
                <div>
                  <p className="font-medium text-foreground">{member?.name}</p>
                  <p className="text-xs text-muted-foreground">HR Specialist</p>
                </div>
              </div>
              <div className="flex items-center space-x-6 text-sm">
                <div className="text-center">
                  <p className="font-semibold text-foreground">{member?.processed}</p>
                  <p className="text-xs text-muted-foreground">Processed</p>
                </div>
                <div className="text-center">
                  <p className="font-semibold text-success">{member?.accuracy}%</p>
                  <p className="text-xs text-muted-foreground">Accuracy</p>
                </div>
                <div className="text-center">
                  <p className="font-semibold text-secondary">{member?.interviews}</p>
                  <p className="text-xs text-muted-foreground">Interviews</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsChart;